/**
 * Dual-fires GTM dataLayer + Microsoft Clarity custom events.
 * Event names follow GA4 recommended ecommerce where they exist so GTM
 * tags can be mapped 1:1. Analyzify `ee_*` events are left untouched.
 *
 * ponytail: 800ms event-key dedupe. Upgrade: per-event TTL map if two
 * legitimate identical events can fire inside that window.
 */
(function (window, document) {
  'use strict';

  var CONFIG_ID = 'ez-journey-tracker-config';
  var DEDUPE_MS = 800;
  var PII_KEYS = /^(email|phone|first_name|last_name|address|zip|postal)/i;

  var EVENTS = {
    PAGE_VIEW: 'ez_page_view',
    VIEW_ITEM_LIST: 'view_item_list',
    VIEW_ITEM: 'view_item',
    VIEW_SEARCH_RESULTS: 'view_search_results',
    VIEW_CART: 'view_cart',
    SELECT_ITEM: 'select_item',
    ADD_TO_CART: 'add_to_cart',
    REMOVE_FROM_CART: 'remove_from_cart',
    UPDATE_CART: 'update_cart',
    BEGIN_CHECKOUT: 'begin_checkout',
    ADD_TO_WISHLIST: 'add_to_wishlist',
    SEARCH: 'search',
    APPLY_FILTER: 'apply_filter',
    CLEAR_FILTER: 'clear_filter',
    OPEN_MINI_CART: 'open_mini_cart',
    OPEN_QUICK_VIEW: 'open_quick_view',
    GENERATE_LEAD: 'generate_lead',
    LOGIN: 'login',
    SIGN_UP: 'sign_up',
    SELECT_STORE: 'select_store',
    CLICK_NAV: 'click_nav',
    VIEW_CHECKOUT: 'view_checkout',
    ADD_SHIPPING_INFO: 'add_shipping_info',
    ADD_PAYMENT_INFO: 'add_payment_info',
    PURCHASE: 'purchase'
  };

  var ECOMMERCE_EVENTS = {
    view_item_list: true,
    view_item: true,
    view_search_results: true,
    view_cart: true,
    select_item: true,
    add_to_cart: true,
    remove_from_cart: true,
    update_cart: true,
    begin_checkout: true,
    add_to_wishlist: true,
    add_shipping_info: true,
    add_payment_info: true,
    purchase: true
  };

  var lastKey = '';
  var lastAt = 0;
  var started = false;
  /** @type {object} */
  var config = {};

  /**
   * @returns {object}
   */
  function readConfig() {
    var node = document.getElementById(CONFIG_ID);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  /**
   * @returns {boolean}
   */
  function debugEnabled() {
    if (config.debug) return true;
    try {
      return new URLSearchParams(window.location.search).has('ez_journey_debug');
    } catch (error) {
      return false;
    }
  }

  /**
   * @param {unknown} value
   * @returns {number}
   */
  function toMoney(value) {
    if (typeof value === 'number' && isFinite(value)) {
      return Math.round(value * 100) / 100;
    }
    var parsed = parseFloat(String(value || '').replace(/[^0-9.]/g, ''));
    return isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
  }

  /**
   * @param {object} payload
   * @returns {object}
   */
  function stripPii(payload) {
    var clean = {};
    if (!payload || typeof payload !== 'object') return clean;
    for (var key in payload) {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
      if (PII_KEYS.test(key)) continue;
      var value = payload[key];
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        clean[key] = stripPii(value);
      } else {
        clean[key] = value;
      }
    }
    return clean;
  }

  /**
   * @param {object} [item]
   * @returns {object|null}
   */
  function ga4Item(item) {
    if (!item) return null;
    var out = {
      item_id: String(item.item_id || item.id || item.product_id || ''),
      item_name: item.item_name || item.name || item.title || '',
      price: toMoney(item.price),
      quantity: Number(item.quantity) || 1
    };
    if (item.item_brand || item.brand) out.item_brand = item.item_brand || item.brand;
    if (item.item_category || item.category) out.item_category = item.item_category || item.category;
    if (item.item_variant || item.variant_title || item.variant) {
      out.item_variant = item.item_variant || item.variant_title || item.variant;
    }
    if (item.item_sku || item.sku) out.item_sku = item.item_sku || item.sku;
    if (item.item_list_id || item.list_id) out.item_list_id = item.item_list_id || item.list_id;
    if (item.item_list_name || item.list_name) out.item_list_name = item.item_list_name || item.list_name;
    if (item.index) out.index = Number(item.index);
    if (item.variant_id) out.item_variant_id = String(item.variant_id);
    return out.item_id || out.item_name ? out : null;
  }

  /**
   * @param {Element|null} node
   * @returns {object|null}
   */
  function itemFromNode(node) {
    var card = node && node.closest ? node.closest('[data-ez-track-item]') : null;
    if (!card) return null;
    return ga4Item({
      item_id: card.getAttribute('data-ez-item-id'),
      variant_id: card.getAttribute('data-ez-item-variant-id'),
      name: card.getAttribute('data-ez-item-name'),
      brand: card.getAttribute('data-ez-item-brand'),
      category: card.getAttribute('data-ez-item-category'),
      sku: card.getAttribute('data-ez-item-sku'),
      price: card.getAttribute('data-ez-item-price'),
      handle: card.getAttribute('data-ez-item-handle'),
      list_id: card.getAttribute('data-ez-item-list-id') || listIdFromPage(),
      list_name: card.getAttribute('data-ez-item-list-name') || listNameFromPage()
    });
  }

  /**
   * @returns {string}
   */
  function listIdFromPage() {
    if (config.collection && config.collection.handle) return config.collection.handle;
    if (config.page_type === 'search') return 'search';
    return config.page_type || 'other';
  }

  /**
   * @returns {string}
   */
  function listNameFromPage() {
    if (config.collection && config.collection.title) return config.collection.title;
    if (config.page_type === 'search') return 'Search';
    return config.page_type || 'other';
  }

  /**
   * @param {object} [variant]
   * @param {number} [quantity]
   * @returns {object|null}
   */
  function itemFromVariant(variant, quantity) {
    if (!variant) {
      if (config.page_type !== 'product' || !config.product) return null;
      return ga4Item({
        item_id: config.product.id,
        name: config.product.name,
        brand: config.product.brand,
        category: config.product.category,
        sku: config.product.sku,
        price: config.product.price,
        variant_id: config.product.variant_id,
        variant_title: config.product.variant_title,
        quantity: quantity
      });
    }

    var price = variant.price;
    if (typeof price === 'number' && price > 1000) price = price / 100;

    return ga4Item({
      item_id: variant.product_id || (config.product && config.product.id) || variant.id,
      name: variant.product_title || variant.title || (config.product && config.product.name),
      brand: variant.vendor || (config.product && config.product.brand),
      category: variant.product_type || (config.product && config.product.category),
      sku: variant.sku,
      price: price,
      variant_id: variant.id || variant.variant_id,
      variant_title: variant.public_title || variant.variant_title || variant.title,
      quantity: quantity || variant.quantity
    });
  }

  /**
   * @returns {object[]}
   */
  function cartItems() {
    var items = (config.cart && config.cart.items) || [];
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var mapped = ga4Item(items[i]);
      if (mapped) out.push(mapped);
    }
    return out;
  }

  /**
   * @param {object[]} items
   * @returns {number}
   */
  function itemsValue(items) {
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      total += toMoney(items[i].price) * (Number(items[i].quantity) || 1);
    }
    return Math.round(total * 100) / 100;
  }

  /**
   * @param {string} eventName
   * @param {object} [payload]
   */
  function track(eventName, payload) {
    if (!eventName) return;
    payload = payload || {};

    var key = eventName + ':' + JSON.stringify({
      id: payload.item_id || payload.search_term || payload.step || payload.link_url || '',
      value: payload.value || 0
    });
    var now = Date.now();
    if (key === lastKey && now - lastAt < DEDUPE_MS) return;
    lastKey = key;
    lastAt = now;

    var dataLayer = (window.dataLayer = window.dataLayer || []);
    var ecommerce = null;
    if (ECOMMERCE_EVENTS[eventName]) {
      ecommerce = {
        currency: payload.currency || config.currency || 'USD',
        value: toMoney(payload.value)
      };
      if (payload.items) ecommerce.items = payload.items;
      if (payload.item_list_id) ecommerce.item_list_id = payload.item_list_id;
      if (payload.item_list_name) ecommerce.item_list_name = payload.item_list_name;
      if (payload.transaction_id) ecommerce.transaction_id = String(payload.transaction_id);
      if (payload.shipping != null) ecommerce.shipping = toMoney(payload.shipping);
      if (payload.tax != null) ecommerce.tax = toMoney(payload.tax);
      dataLayer.push({ ecommerce: null });
    }

    var gtmEvent = {
      event: eventName,
      page_type: config.page_type,
      ez_source: 'theme_journey'
    };
    if (ecommerce) gtmEvent.ecommerce = ecommerce;
    if (payload.search_term) gtmEvent.search_term = payload.search_term;
    if (payload.filter_name) gtmEvent.filter_name = payload.filter_name;
    if (payload.filter_value) gtmEvent.filter_value = payload.filter_value;
    if (payload.method) gtmEvent.method = payload.method;
    if (payload.step) gtmEvent.checkout_step = payload.step;
    if (payload.link_text) gtmEvent.link_text = payload.link_text;
    if (payload.link_url) gtmEvent.link_url = payload.link_url;
    if (payload.store_name) gtmEvent.store_name = payload.store_name;
    if (payload.lead_type) gtmEvent.lead_type = payload.lead_type;
    dataLayer.push(gtmEvent);

    if (typeof window.clarity === 'function') {
      window.clarity('event', eventName);
      var tags = stripPii({
        page_type: config.page_type,
        search_term: payload.search_term,
        checkout_step: payload.step,
        lead_type: payload.lead_type,
        filter_name: payload.filter_name,
        store_name: payload.store_name,
        item_id: payload.items && payload.items[0] && payload.items[0].item_id
      });
      for (var tag in tags) {
        if (tags[tag] != null && tags[tag] !== '') {
          window.clarity('set', tag, String(tags[tag]));
        }
      }
    }

    if (debugEnabled()) {
      console.info('[ez-journey]', eventName, gtmEvent);
    }
  }

  function firePageEvents() {
    track(EVENTS.PAGE_VIEW, {});

    if (config.product) {
      var productItem = ga4Item(config.product);
      track(EVENTS.VIEW_ITEM, {
        items: productItem ? [productItem] : [],
        value: config.product.price
      });
    }

    if (config.page_type === 'collection' && config.collection) {
      track(EVENTS.VIEW_ITEM_LIST, {
        item_list_id: config.collection.handle,
        item_list_name: config.collection.title,
        items: []
      });
    }

    if (config.page_type === 'search') {
      track(EVENTS.VIEW_SEARCH_RESULTS, {
        search_term: config.search_term || '',
        items: []
      });
    }

    if (config.page_type === 'cart') {
      var items = cartItems();
      track(EVENTS.VIEW_CART, {
        items: items,
        value: (config.cart && config.cart.value) || itemsValue(items)
      });
    }
  }

  function waitForCheckout() {
    if (!config.checkout) return;
    var tries = 0;
    var timer = window.setInterval(function () {
      tries += 1;
      if (window.Shopify && window.Shopify.Checkout) {
        window.clearInterval(timer);
        fireCheckoutEvents();
        return;
      }
      if (tries > 20) {
        window.clearInterval(timer);
        var fallbackItems = [];
        var checkoutItems = (config.checkout && config.checkout.items) || [];
        for (var i = 0; i < checkoutItems.length; i++) {
          var mapped = ga4Item(checkoutItems[i]);
          if (mapped) fallbackItems.push(mapped);
        }
        track(EVENTS.VIEW_CHECKOUT, {
          items: fallbackItems,
          value: config.checkout && config.checkout.value,
          step: 'checkout'
        });
      }
    }, 250);
  }

  function fireCheckoutEvents() {
    var checkout = window.Shopify && window.Shopify.Checkout;
    if (!checkout) return;

    var step = checkout.step || '';
    var page = checkout.page || '';
    var payload = config.checkout || {};
    var items = [];
    if (payload.items) {
      for (var i = 0; i < payload.items.length; i++) {
        var mapped = ga4Item(payload.items[i]);
        if (mapped) items.push(mapped);
      }
    }

    var base = {
      items: items,
      value: payload.value,
      shipping: payload.shipping,
      tax: payload.tax,
      currency: payload.currency,
      step: step || page
    };

    track(EVENTS.VIEW_CHECKOUT, base);

    if (step === 'shipping_method') track(EVENTS.ADD_SHIPPING_INFO, base);
    if (step === 'payment_method') track(EVENTS.ADD_PAYMENT_INFO, base);

    if (page === 'thank_you' || step === 'thank_you') {
      var orderId = payload.transaction_id;
      var storageKey = 'ez_journey_purchase_' + String(orderId || checkout.token || '');
      try {
        if (window.sessionStorage.getItem(storageKey)) return;
        window.sessionStorage.setItem(storageKey, '1');
      } catch (error) {}
      base.transaction_id = orderId;
      track(EVENTS.PURCHASE, base);
    }
  }

  /**
   * @param {Element} target
   * @returns {boolean}
   */
  function isCheckoutControl(target) {
    if (target.getAttribute('data-ez-track') === 'begin_checkout') return true;
    if (target.getAttribute('name') === 'checkout') return true;
    if (target.classList.contains('cart-recap__checkout')) return true;
    var href = target.getAttribute('href') || '';
    return href === '/checkout' || href.indexOf('/checkout') === 0;
  }

  /**
   * @param {Element} target
   * @returns {boolean}
   */
  function isRemoveControl(target) {
    var action = target.getAttribute('data-action') || '';
    if (action === 'decrease-quantity' && target.getAttribute('data-quantity') === '0') return true;
    if ((target.getAttribute('href') || '').indexOf('/cart/change') !== -1 && (target.getAttribute('href') || '').indexOf('quantity=0') !== -1) {
      return true;
    }
    return /remove/i.test(target.className) || /remove/i.test(target.getAttribute('aria-label') || '');
  }

  function bindClicks() {
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var control = target.closest('a, button, [data-action], [data-ez-track], [data-swaction], .wizzy-facet-list-item, .wizzy-filters-clear-all, .wizzy-quick-view-button');
      if (!control) return;

      if (control.closest('.swym-add-to-wishlist-view-product') || control.getAttribute('data-swaction') === 'addToWishlist' || control.hasAttribute('data-ez-pdp-wishlist')) {
        var wishItem = itemFromNode(control) || itemFromVariant(null, 1);
        track(EVENTS.ADD_TO_WISHLIST, {
          items: wishItem ? [wishItem] : [],
          value: wishItem ? wishItem.price : 0
        });
        return;
      }

      if (control.getAttribute('data-action') === 'add-to-cart' || control.hasAttribute('data-ez-pdp-add-to-cart')) {
        if (config.page_type === 'product') return;
        var atcItem = itemFromNode(control);
        if (!atcItem) return;
        track(EVENTS.ADD_TO_CART, {
          items: [atcItem],
          value: atcItem.price * (atcItem.quantity || 1)
        });
        return;
      }

      if (control.hasAttribute('data-ez-pdp-interest-submit')) {
        track(EVENTS.GENERATE_LEAD, { lead_type: 'product_interest', method: 'pdp_form' });
        return;
      }

      if (control.hasAttribute('data-ez-pdp-interest-open')) {
        track(EVENTS.GENERATE_LEAD, { lead_type: 'product_interest_open', method: 'pdp' });
        return;
      }

      if (control.getAttribute('data-action') === 'toggle-mini-cart') {
        var mini = document.getElementById('mini-cart');
        var opening = !mini || mini.getAttribute('aria-hidden') !== 'false';
        if (opening) track(EVENTS.OPEN_MINI_CART, { value: config.cart && config.cart.value });
        return;
      }

      if (control.classList.contains('wizzy-quick-view-button') || control.classList.contains('product-item__quick-view-btn') || control.getAttribute('data-action') === 'open-modal') {
        var qvItem = itemFromNode(control);
        track(EVENTS.OPEN_QUICK_VIEW, {
          items: qvItem ? [qvItem] : [],
          value: qvItem ? qvItem.price : 0
        });
        return;
      }

      if (isCheckoutControl(control)) {
        var checkoutItems = cartItems();
        track(EVENTS.BEGIN_CHECKOUT, {
          items: checkoutItems,
          value: (config.cart && config.cart.value) || itemsValue(checkoutItems)
        });
        return;
      }

      if (isRemoveControl(control)) {
        var removed = itemFromLineControl(control);
        track(EVENTS.REMOVE_FROM_CART, {
          items: removed ? [removed] : [],
          value: removed ? removed.price * (removed.quantity || 1) : 0
        });
        return;
      }

      var qtyAction = control.getAttribute('data-action');
      if (qtyAction === 'increase-quantity' || qtyAction === 'decrease-quantity') {
        var qtyItem = itemFromLineControl(control);
        track(EVENTS.UPDATE_CART, {
          items: qtyItem ? [qtyItem] : [],
          value: qtyItem ? qtyItem.price : 0,
          method: qtyAction
        });
        return;
      }

      if (control.classList.contains('wizzy-facet-list-item')) {
        track(EVENTS.APPLY_FILTER, {
          filter_name: control.getAttribute('data-key') || control.getAttribute('title') || 'facet',
          filter_value: (control.getAttribute('title') || control.textContent || '').trim().slice(0, 80)
        });
        return;
      }

      if (control.classList.contains('wizzy-filters-clear-all')) {
        track(EVENTS.CLEAR_FILTER, { method: 'clear_all' });
        return;
      }

      var itemCard = control.closest('[data-ez-track-item]');
      if (itemCard && control.closest('a[href*="/products/"]')) {
        var selected = itemFromNode(control);
        track(EVENTS.SELECT_ITEM, {
          items: selected ? [selected] : [],
          value: selected ? selected.price : 0,
          item_list_id: listIdFromPage(),
          item_list_name: listNameFromPage()
        });
        return;
      }

      var headerLink = control.closest('.header a[href], .ez-header a[href], .footer a[href]');
      if (headerLink && !headerLink.closest('[data-ez-track-item], [data-action], form, .header__action-item')) {
        var href = headerLink.getAttribute('href') || '';
        if (href.indexOf('/products/') !== -1) return;
        track(EVENTS.CLICK_NAV, {
          link_text: (headerLink.textContent || '').trim().slice(0, 80),
          link_url: href.split('?')[0]
        });
      }
    }, true);
  }

  /**
   * @param {Element} control
   * @returns {object|null}
   */
  function itemFromLineControl(control) {
    var fromCard = itemFromNode(control);
    if (fromCard) return fromCard;
    var line = control.closest('.mini-cart__line-item, .line-item, [data-line-item]');
    if (!line) {
      var lineId = control.getAttribute('data-line-id');
      if (lineId && config.cart && config.cart.items) {
        for (var i = 0; i < config.cart.items.length; i++) {
          if (String(config.cart.items[i].variant_id) === String(lineId)) {
            return ga4Item(config.cart.items[i]);
          }
        }
      }
      return null;
    }
    var title = line.querySelector('.mini-cart__product-title, .line-item__title, a');
    return ga4Item({
      name: title ? title.textContent.trim() : '',
      quantity: 1
    });
  }

  function bindThemeEvents() {
    document.addEventListener('product:added', function (event) {
      var detail = event.detail || {};
      var item = itemFromVariant(detail.variant, detail.quantity);
      if (!item && event.target && event.target.closest) {
        item = itemFromNode(event.target);
        if (item && detail.quantity) item.quantity = detail.quantity;
      }
      if (!item) return;
      track(EVENTS.ADD_TO_CART, {
        items: [item],
        value: item.price * (item.quantity || 1)
      });
    });

    document.addEventListener('variant:changed', function (event) {
      var detail = event.detail || {};
      var item = itemFromVariant(detail.variant, 1);
      track(EVENTS.VIEW_ITEM, {
        items: item ? [item] : [],
        value: item ? item.price : 0
      });
    });

    document.addEventListener('wizzy:location-updated', function (event) {
      var detail = event.detail || {};
      track(EVENTS.SELECT_STORE, {
        store_name: detail.storeName || detail.name || detail.zip || 'location'
      });
    });
  }

  function bindForms() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.getAttribute) return;
      var id = form.id || '';
      var action = form.getAttribute('action') || '';

      if (form.querySelector('[name="q"]') && (form.getAttribute('role') === 'search' || action.indexOf('/search') !== -1)) {
        var query = form.querySelector('[name="q"]');
        track(EVENTS.SEARCH, { search_term: query && query.value ? query.value.trim() : '' });
        return;
      }

      if (form.classList.contains('newsletter__form') || (form.querySelector('[name="contact[tags]"]') && (form.querySelector('[name="contact[tags]"]').value || '').indexOf('newsletter') !== -1)) {
        track(EVENTS.GENERATE_LEAD, { lead_type: 'newsletter', method: 'form' });
        return;
      }

      if (id === 'ez-pdp-interest-form') {
        track(EVENTS.GENERATE_LEAD, { lead_type: 'product_interest', method: 'pdp_form' });
        return;
      }

      if (id === 'contact_form' || action.indexOf('/contact') !== -1) {
        track(EVENTS.GENERATE_LEAD, { lead_type: 'contact', method: 'form' });
        return;
      }

      if (id === 'customer_login' || id === 'header_customer_login') {
        track(EVENTS.LOGIN, { method: 'shopify' });
        return;
      }

      if (id === 'create_customer') {
        track(EVENTS.SIGN_UP, { method: 'shopify' });
      }
    }, true);
  }

  function identifyClarity() {
    if (typeof window.clarity !== 'function') return;
    window.clarity('set', 'page_type', String(config.page_type || 'other'));
    window.clarity('set', 'customer_type', String(config.customer_type || 'visitor'));
    if (config.customer_id) {
      window.clarity('identify', String(config.customer_id));
    }
  }

  /**
   * Smallest check that fails if the event contract breaks.
   * Run: window.ezJourney.selfCheck()
   * @returns {boolean}
   */
  function selfCheck() {
    var required = ['ez_page_view', 'view_item', 'add_to_cart', 'begin_checkout', 'purchase'];
    for (var i = 0; i < required.length; i++) {
      var found = false;
      for (var key in EVENTS) {
        if (EVENTS[key] === required[i]) found = true;
      }
      if (!found) {
        console.assert(false, '[ez-journey] missing event ' + required[i]);
        return false;
      }
    }
    console.assert(typeof window.clarity === 'function' || typeof window.clarity === 'undefined', '[ez-journey] clarity stub');
    if (debugEnabled()) console.info('[ez-journey] selfCheck ok', EVENTS);
    return true;
  }

  function start() {
    if (started) return;
    started = true;
    config = readConfig();
    if (!config.search_term) {
      try {
        config.search_term = new URLSearchParams(window.location.search).get('q');
      } catch (error) {}
    }
    identifyClarity();
    firePageEvents();
    waitForCheckout();
    bindClicks();
    bindThemeEvents();
    bindForms();
    selfCheck();
  }

  window.ezJourney = {
    track: track,
    events: EVENTS,
    selfCheck: selfCheck
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})(window, document);
